// Plan property limit.
//
// Every plan caps how many properties a landlord may list (`plan.property_limit`
// on the subscription that the login response stores in localStorage). The
// backend is the source of truth, but the screens check the cap up front so the
// landlord is told before filling in the whole Add Property form.

import { getUserDetails } from './postLoginNavigation';
import { isLiveSubscriptionStatus } from './subscriptionTrial';

const readStoredUser = () => {
  for (const key of ['userData', 'loginResponse']) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const details = getUserDetails(JSON.parse(raw));
      if (details && Object.keys(details).length) return details;
    } catch (parseError) {
      console.error(`Error reading ${key} from storage:`, parseError);
    }
  }
  return {};
};

/**
 * The subscription the cap comes from: the live one (active or trialing) when
 * there is one, otherwise whatever is stored so an expired plan still reports a
 * limit instead of looking unlimited.
 */
export const getActiveSubscription = (details = readStoredUser()) => {
  const subscriptions = details?.UserSubscription;
  if (!subscriptions) return null;
  if (!Array.isArray(subscriptions)) return subscriptions;
  return subscriptions.find((sub) => isLiveSubscriptionStatus(sub?.status)) || subscriptions[0] || null;
};

/** Plan name ("Basic", "Pro", ...) for the limit message, or '' when unknown. */
export const getPlanName = () => getActiveSubscription()?.plan?.name || '';

/**
 * How many properties the current plan allows.
 *
 * @returns {number|null} The cap, or null when there is no usable number
 *                        (no subscription stored, or an unlimited plan). null
 *                        means "do not block" - the API still has the final say.
 */
export const getPropertyLimit = () => {
  const subscription = getActiveSubscription();
  if (!subscription) return null;

  const raw = subscription?.plan?.property_limit ?? subscription?.property_limit;
  const limit = Number(raw);
  if (!Number.isFinite(limit) || limit <= 0) return null;
  return limit;
};

/** True once the landlord already has as many properties as the plan allows. */
export const hasReachedPropertyLimit = (propertyCount) => {
  const limit = getPropertyLimit();
  if (limit === null) return false;
  const count = Number(propertyCount);
  if (!Number.isFinite(count)) return false;
  return count >= limit;
};

/** The message shown on the disabled Add Property button and on the guard toast. */
export const getPropertyLimitMessage = () => {
  const limit = getPropertyLimit();
  const planName = getPlanName();
  const plan = planName ? `${planName} plan` : 'current plan';
  return `You have reached the ${plan} limit of ${limit} ${limit === 1 ? 'property' : 'properties'}. Upgrade your plan in Profile → Subscription to add more.`;
};

/**
 * Writes a newly chosen plan onto the stored subscription so the cap follows a
 * plan change without a fresh login. Both storage keys are updated because
 * readStoredUser() falls back from one to the other. The plan object is
 * replaced, not merged: if the new plan carries no `property_limit`, an old
 * cap must not linger - the API still enforces the real one.
 */
export const updateStoredSubscriptionPlan = (plan) => {
  if (!plan) return;
  for (const key of ['userData', 'loginResponse']) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const stored = JSON.parse(raw);
      const details = getUserDetails(stored);
      const subscriptions = details?.UserSubscription;
      if (!subscriptions) continue;

      const current = getActiveSubscription(details);
      const withNewPlan = (sub) => {
        if (sub !== current) return sub;
        const { property_limit: _staleLimit, ...rest } = sub;
        return { ...rest, plan_id: plan.id ?? rest.plan_id, plan: { ...plan } };
      };
      details.UserSubscription = Array.isArray(subscriptions)
        ? subscriptions.map(withNewPlan)
        : withNewPlan(subscriptions);

      localStorage.setItem(key, JSON.stringify(stored));
    } catch (storageError) {
      console.error(`Error updating plan in ${key}:`, storageError);
    }
  }
};
